import React, { memo } from 'react'
import tw from 'twin.macro'
import { useFormikContext } from 'formik'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { DataRow } from 'src/components/shared/DataRow'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import {
  BUSINESS_START_YEAR_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  LIMIT_SOURCE_OPTIONS,
  MAX_REQUESTED_CREDIT_LIMIT_CENTS,
  MONTH_OPTIONS,
  REQUESTED_CREDIT_LIMIT_UNIT_NOTE,
} from 'src/constants/oatfi-business/constants'
import { US_STATE_OPTIONS } from 'src/constants/oatfi-business/states'
import type { OatfiBusinessFormFields } from 'src/interfaces/oatfiBusiness'
import { formatCentsAsDollars } from 'src/utils/oatfiBusiness'
import type { BusinessProfileFormMode } from './BusinessProfileForm.interface'

interface Props {
  mode: BusinessProfileFormMode
  // Without `oatfi_manage`: every field disabled and no submit.
  isReadOnly: boolean
  isSaving: boolean
  isBankAccountLinked: boolean
}

// Same rhythm as the Settings page: a section is an h5 heading and its fields 16px apart, and
// sections sit 32px apart. Pairs that belong together (city/state, first/last name, month/year)
// share a row on wider screens.
const sectionStyles = tw`flex flex-col gap-4`
const pairStyles = tw`grid grid-cols-1 gap-4 md:grid-cols-2`

/**
 * Neither `BasicTextField` nor `BasicSelect` passes `label` to a MUI component that would draw
 * its own asterisk — both draw the label in their own plain `Typography`. Baking the marker into
 * the label string is what actually surfaces it for both field types, without touching either
 * shared component (both have existing callers).
 */
const requiredLabel = (text: string) => {
  return `${text} *`
}

const BusinessProfileFormContentComponent = ({
  mode,
  isReadOnly,
  isSaving,
  isBankAccountLinked,
}: Props) => {
  const { values } = useFormikContext<OatfiBusinessFormFields>()

  /**
   * The live dollar echo, which is the guard against a 100x slip on a lender-facing figure.
   *
   * It deliberately shows nothing for a value the validator will reject — over the int4 storage
   * ceiling, or not all digits. Echoing a figure there would be worse than silence: `Number()`
   * loses precision above `Number.MAX_SAFE_INTEGER`, so the hint would state a dollar amount
   * that is not what was typed and not what would be stored. The validator's own message is
   * what the admin needs to see in that state.
   */
  const enteredCreditLimitCents = /^\d+$/.test(values.requestedCreditLimit)
    ? Number(values.requestedCreditLimit)
    : null
  const creditLimitDollars =
    enteredCreditLimitCents !== null && enteredCreditLimitCents <= MAX_REQUESTED_CREDIT_LIMIT_CENTS
      ? formatCentsAsDollars(enteredCreditLimitCents)
      : null
  const creditLimitHint =
    creditLimitDollars === null
      ? `Whole number of ${REQUESTED_CREDIT_LIMIT_UNIT_NOTE}.`
      : `${creditLimitDollars} — entered in ${REQUESTED_CREDIT_LIMIT_UNIT_NOTE}.`

  return (
    <div css={tw`flex flex-col gap-8`}>
      <div css={sectionStyles}>
        <Typography variant="h5" css={tw`text-shades-1000`}>
          Business
        </Typography>
        <div css={pairStyles}>
          <FormikTextField<OatfiBusinessFormFields>
            name="name"
            label={requiredLabel('Name')}
            disabled={isReadOnly}
          />
          <FormikTextField<OatfiBusinessFormFields>
            name="dbaName"
            label="DBA name"
            disabled={isReadOnly}
          />
          <FormikTextField<OatfiBusinessFormFields>
            name="taxId"
            label={requiredLabel('Tax ID')}
            disabled={isReadOnly}
          />
          <FormikSelect<OatfiBusinessFormFields>
            name="businessEntityType"
            label="Entity type"
            options={ENTITY_TYPE_OPTIONS}
            placeholder="Select an entity type"
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div css={sectionStyles}>
        <Typography variant="h5" css={tw`text-shades-1000`}>
          Address
        </Typography>
        <FormikTextField<OatfiBusinessFormFields>
          name="streetAddress"
          label={requiredLabel('Street address')}
          disabled={isReadOnly}
        />
        <div css={tw`grid grid-cols-1 gap-4 md:grid-cols-3`}>
          <FormikTextField<OatfiBusinessFormFields>
            name="city"
            label={requiredLabel('City')}
            disabled={isReadOnly}
          />
          <FormikSelect<OatfiBusinessFormFields>
            name="state"
            label={requiredLabel('State')}
            options={US_STATE_OPTIONS}
            placeholder={requiredLabel('Select a state')}
            disabled={isReadOnly}
          />
          <FormikTextField<OatfiBusinessFormFields>
            name="postalCode"
            label={requiredLabel('Postal code')}
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div css={sectionStyles}>
        <Typography variant="h5" css={tw`text-shades-1000`}>
          Contact
        </Typography>
        <div css={pairStyles}>
          <FormikTextField<OatfiBusinessFormFields>
            name="contactFirstName"
            label={requiredLabel('Contact first name')}
            disabled={isReadOnly}
          />
          <FormikTextField<OatfiBusinessFormFields>
            name="contactLastName"
            label={requiredLabel('Contact last name')}
            disabled={isReadOnly}
          />
          <div>
            <FormikTextField<OatfiBusinessFormFields>
              name="contactPhoneNumber"
              label={requiredLabel('Contact phone number')}
              disabled={isReadOnly}
            />
            <Typography variant="body2" css={tw`mt-1 text-shades-500`}>
              US numbers only.
            </Typography>
          </div>
          <FormikTextField<OatfiBusinessFormFields>
            name="contactEmail"
            label="Contact email"
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div css={sectionStyles}>
        <Typography variant="h5" css={tw`text-shades-1000`}>
          Trading history
        </Typography>
        <div css={pairStyles}>
          <FormikSelect<OatfiBusinessFormFields>
            name="businessStartDateMonth"
            label="Start month"
            options={MONTH_OPTIONS}
            placeholder="Select a month"
            disabled={isReadOnly}
          />
          <FormikSelect<OatfiBusinessFormFields>
            name="businessStartDateYear"
            label="Start year"
            options={BUSINESS_START_YEAR_OPTIONS}
            placeholder="Select a year"
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div css={sectionStyles}>
        <Typography variant="h5" css={tw`text-shades-1000`}>
          Requested credit
        </Typography>
        <div>
          <FormikSelect<OatfiBusinessFormFields>
            name="requestedLimitSource"
            label="Limit source"
            options={LIMIT_SOURCE_OPTIONS}
            placeholder="Select a limit source"
            disabled={isReadOnly}
          />
          {/*
            FormikSelect's Props extend MUI's SelectProps, which has no `helperText` — rendered as
            a sibling hint instead of a helperText prop that would not compile.
          */}
          <Typography variant="body2" css={tw`mt-1 text-shades-500`}>
            &apos;GigSafe&apos; means GigSafe set the limit; &apos;Customer requested&apos; means
            the business asked for it.
          </Typography>
        </div>
        {/* The unit is in the label AND echoed as dollars in a sibling hint, not a `helperText`
            prop: BasicTextField only renders helperText when the field is ALSO in an error state
            (`!!error && !!helperText`), and FormikTextField's own helperText computation is
            overwritten by whatever the caller passes via its later `{...rest}` spread — so a
            helperText prop here would both stay invisible during normal entry and clobber the
            real yup error message when the field is invalid. The wire enforces nothing on this
            field, so a bare number input is how a 100x error reaches a lender — an admin typing
            50000 must see "$500.00" here, live, to catch it. */}
        <div>
          <FormikTextField<OatfiBusinessFormFields>
            name="requestedCreditLimit"
            label={`Requested credit limit (${REQUESTED_CREDIT_LIMIT_UNIT_NOTE})`}
            disabled={isReadOnly}
          />
          <Typography variant="body2" css={tw`mt-1 text-shades-500`}>
            {creditLimitHint}
          </Typography>
        </div>
        {/* plaidToken is on the response but not the create/update DTO, so it is reported, never
            set. The token value itself is opaque and is deliberately never rendered. */}
        <DataRow
          title="Bank account"
          text={isBankAccountLinked ? 'Linked' : 'Not linked'}
          dataTestId="business-plaid-token-state"
        />
      </div>

      {isReadOnly ? null : (
        <div css={tw`flex flex-row justify-end`}>
          <Button
            type="submit"
            size="small"
            color="dark"
            className={tw`px-4`}
            loading={isSaving}
            disabled={isSaving}
            data-testid="business-profile-submit"
          >
            {mode === 'create' ? 'Create' : 'Save changes'}
          </Button>
        </div>
      )}
    </div>
  )
}

export const BusinessProfileFormContent = memo(BusinessProfileFormContentComponent)
